import { FormEvent, useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Img } from 'react-image';
import classNames from 'classnames';
import { RegisterUserInput, UserExperienceLevelType } from 'types';
import { useMutation } from 'react-query';
import { registerUser } from 'src/apis/user';
import { useToast } from 'src/context/ToastProvider';
import { useUserContext } from 'src/context/UserProvider';
import RegisterForm from './RegisterForm';
import FloatingCompany from '../shared/FloatingCompany';
import { get } from 'lodash';
import PhoneVerification from './PhoneVerification';
import ChangePhoneForm from './ChangePhone';
import { sendTrackingEvent } from 'src/utils/gtm';
import {
	WEBSITE_URL,
	isRecruiterSide,
	RECRUITER_APP_ENDPOINT,
	UNIVERSITY_APP_ENDPOINT,
	STUDENT_APP_ENDPOINT,
	isUniversitySide,
} from 'src/config/env.config';
import ChangeEmailForm from './ChangeEmail';
import { useThemeContext } from 'src/context/ThemeProvider';
import AuthSelector from './AuthSelector';
import { sendMixpanelTrackingEvent } from 'src/utils/mixpanel';

const SignupForm = ({ showFormUiOnly = false }: { showFormUiOnly?: boolean }) => {
	const location = useLocation();
	const navigate = useNavigate();
	const { user } = useUserContext();
	const [searchParamas] = useSearchParams();
	const toast = useToast();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [otpSubmitted, setOtpSubmitted] = useState<boolean>(false);
	const [emailOrPhone, setEmailOrPhone] = useState<boolean>(false);
	const searchParams = new URLSearchParams(window.location.search);
	const [userInfo, setUserInfo] = useState<RegisterUserInput>({
		name: '',
		email: '',
		phone: '',
		experience: '0',
		usedReferralCode: '',
		companyName: '',
		designation: '',
		experienceLevel: '' as UserExperienceLevelType,
	});
	const redirectUrl = searchParamas.get('redirect_url');

	const signupUserFn = useMutation({
		mutationKey: 'registerUser',
		mutationFn: registerUser,
		onSuccess(data) {
			toast({
				type: 'success',
				title: `An OTP has sent to your registered ${isRecruiterSide ? 'Email' : 'Phone'}.`,
			});
			// set userId info into localstorage
			localStorage.setItem('userId', data.userId);
			if (redirectUrl?.includes('placement-drive-registration')) {
				console.log('placement-drive-registration triggered');
				sendMixpanelTrackingEvent('website_signup_interview_placement_drive', {
					source: 'Placed_SignUp',
					location: window.location.href,
					userId: data.userId,
				});
				sendTrackingEvent({
					event: 'website_signup_interview_placement_drive',
					eventLabel: location.pathname + location.search,
					userId: data.userId,
				});
			}
			setIsLoading(false);
			setOtpSubmitted(true);
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				type: 'error',
				title: errorMessage,
			});
			setIsLoading(false);
			setOtpSubmitted(false);
		},
	});

	const handleRegister = (event: FormEvent<HTMLFormElement>) => {
		if (isRecruiterSide) {
			sendTrackingEvent({
				event: 'recr_signup_register',
				eventLabel: location.pathname,
			});
		} else {
			sendTrackingEvent({
				event: 'register',
				eventLabel: location.pathname,
			});
		}

		event.preventDefault();
		setIsLoading(true);
		signupUserFn.mutate({ ...userInfo, email: userInfo.email.trim().toLowerCase(), phone: userInfo.phone.trim() });
	};

	useEffect(() => {
		if (searchParams.get('refCode')) {
			setUserInfo({ ...userInfo, usedReferralCode: searchParams.get('refCode') || '' });
			localStorage.setItem('refCode', searchParams.get('refCode') || '');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchParams]);

	useEffect(() => {
		if (user) {
			console.log('user:', user);
			console.log('redirectUrl:', redirectUrl);
			if (redirectUrl) {
				window.open(decodeURIComponent(redirectUrl), '_self');
			} else {
				// navigate('/dashboard');
				navigate('/my-applications');
			}
		}
	});

	useEffect(() => {
		setOtpSubmitted(false);
	}, []);

	useEffect(() => {
		const name = searchParamas.get('name');
		const email = searchParamas.get('email') || '';
		const companyName = searchParamas.get('company');
		const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

		if (name && companyName && emailRegex.test(email.trim().toLowerCase())) {
			sendTrackingEvent({
				event: 'recr_signup_register',
				eventLabel: location.pathname,
			});

			setIsLoading(true);
			signupUserFn.mutate({ ...userInfo, name, companyName, email: email.trim().toLowerCase() });
		}
	}, [searchParamas]);

	useEffect(() => {
		if (isRecruiterSide) {
			window.onload = () => {
				sendTrackingEvent({
					event: 'fb_recr_view_trigger',
					eventLabel: location.pathname,
				});
			};
		}
	}, []);

	const { themeMode } = useThemeContext();
	return (
		<div
			style={
				showFormUiOnly
					? {}
					: {
						minHeight: '100dvh',
						background: `url(${themeMode === 'dark' ? '/assets/app-bg.svg' : '/assets/desktop-bg.svg'}) no-repeat`,
						backgroundSize: 'cover',
					}
			}
			className="h-[100%] flex flex-col m-[auto] md:m-[0px]  min-w-[360px] md:min-w-[400px] justify-start md:justify-evenly items-center relative bg-no-repeat !bg-cover md:pt-[60px] md:!bg-[#E5F0FF] "
		>
			{showFormUiOnly ? null : (
				<div className={'max-w-lg lg:w-full pl-[12px] block w-[100%] mb-[auto] md:mb-[0px] md:mt-[0px]'}>
					<Img
						loading="lazy"
						className={'block md:hidden pt-[20px] pb-[18px] pl-[6px]'}
						src={
							themeMode === 'dark'
								? 'https://masai-website-images.s3.ap-south-1.amazonaws.com/level_Up_By_Masai_Icon_Dark_a5f53496b9.svg'
								: 'https://masai-website-images.s3.ap-south-1.amazonaws.com/level_Up_By_Masai_Icon_Dark_a5f53496b9.svg'
						}
						width="101"
						height="36"
						alt="logo"
					/>
					{isRecruiterSide ? (
						<p
							className={
								'whitespace-pre-wrap text-[32px] md:text-[44px] leading-[40px] font-inter font-[700] dark:text-white text-[#1F3343] authSideInfopara_text md:hidden'
							}
						>
							Match with the{' '}
							<span className="dark:text-[#01b899] bg-dark-gradient-skyblue bg-clip-text text-transparent">
								Tech <br />
								Talent
							</span>{' '}
							you need in <br />
							<span>minutes</span>
						</p>
					) : (
						<p
							className={
								'whitespace-pre-wrap text-[32px] lg:text-[44px] leading-[40px] font-inter font-[700] text-[#1F3343] dark:text-white md:hidden'
							}
						>
							Ready to get your dream
							<span className="text-[#01b899]"> job</span>
						</p>
					)}
					<div
						className={classNames('mb-4 block pt-2 text-base font-medium dark:text-[#C7C9D9] text-[#555770] md:hidden')}
					>
						{!otpSubmitted ? (
							isRecruiterSide ? (
								<div>Choose from a pool of pre-assessed developers that perfectly align with your hiring needs. </div>
							) : (
								<div>Sign up now and start your job seeking journey with our help.</div>
							)
						) : (
							'Get your dream tech job'
						)}
					</div>

					{otpSubmitted && !isRecruiterSide ? (
						<div className="md:hidden mt-[30px]">
							<FloatingCompany />
						</div>
					) : null}

					<div
						className="signIn_logo_div cursor-pointer"
						onClick={() => {
							isRecruiterSide ? window.open(`${WEBSITE_URL}/hire-from-us`) : window.open(`${WEBSITE_URL}/`);
						}}
					>
						<Img
							loading="lazy"
							src={
								themeMode === 'dark'
									? 'https://masai-website-images.s3.ap-south-1.amazonaws.com/level_Up_By_Masai_Icon_Dark_a5f53496b9.svg'
									: 'https://masai-website-images.s3.ap-south-1.amazonaws.com/level_Up_By_Masai_Icon_Dark_a5f53496b9.svg'
							}
							alt="logo"
							width="93"
							height="24"
						/>
					</div>
				</div>
			)}

			<div
				className={classNames(
					'text-[#28293D] dark:text-white rounded-[48px] w-full md:w-max ',
					isRecruiterSide ? 'mt-[10px]' : '',
				)}
			>
				<AuthSelector
					value={
						isUniversitySide
							? {
								label: 'University',
								value: 'University',
								description: (
									<>
										Assess <br /> Students
									</>
								),
							}
							: isRecruiterSide
								? {
									label: 'Recruiter',
									value: 'Recruiter',
									description: (
										<>
											Find <br /> Jobs
										</>
									),
								}
								: {
									label: 'Jobseeker',
									value: 'Jobseeker',
									description: (
										<>
											Hire <br /> Talent
										</>
									),
								}
					}
					options={[
						{
							label: 'Jobseeker',
							value: 'Jobseeker',
							description: (
								<>
									Find <br /> Jobs
								</>
							),
						},
						{
							label: 'Recruiter',
							value: 'Recruiter',
							description: (
								<>
									Hire <br /> Talent
								</>
							),
						},
						{
							label: 'University',
							value: 'University',
							description: (
								<>
									Assess <br /> Students
								</>
							),
						},
					]}
					onChange={data => {
						// presserve search params
						const searchParams = new URLSearchParams(window.location.search);
						const params = new URLSearchParams();
						searchParams.forEach((value, key) => {
							params.append(key, value);
						});
						if (data.value === 'JobSeeker' && (isUniversitySide || isRecruiterSide)) {
							window.open(`${STUDENT_APP_ENDPOINT}/auth/register?${params.toString()}`, '_self');
						} else if (data.value === 'Recruiter' && !isRecruiterSide) {
							window.open(`${RECRUITER_APP_ENDPOINT}/auth/register?${params.toString()}`, '_self');
						} else if (data.value === 'University' && !isUniversitySide) {
							window.open(`${UNIVERSITY_APP_ENDPOINT}/auth/register?${params.toString()}`, '_self');
						}
					}}
					label="Create an account as:"
				/>
			</div>

			{!otpSubmitted ? (
				<RegisterForm
					isLoading={isLoading}
					handleRegister={handleRegister}
					userInfo={userInfo}
					setUserInfo={setUserInfo}
				/>
			) : emailOrPhone ? (
				isRecruiterSide ? (
					<ChangeEmailForm
						setOtpSubmitted={setOtpSubmitted}
						setPhoneOrEmail={setEmailOrPhone}
						userInfo={userInfo}
						setUserInfo={setUserInfo}
					/>
				) : (
					<ChangePhoneForm
						setOtpSubmitted={setOtpSubmitted}
						setPhoneOrEmail={setEmailOrPhone}
						userInfo={userInfo}
						setUserInfo={setUserInfo}
					/>
				)
			) : (
				<PhoneVerification
					emailORPhone={isRecruiterSide ? userInfo.email : userInfo.phone}
					phoneOrEmail={emailOrPhone}
					setPhoneOrEmail={setEmailOrPhone}
				/>
			)}
		</div>
	);
};

export default SignupForm;
